<!--
  Usage Examples:

  For single file uploads (avatars/logos):
  <app-upload-file
    [fileTypeInS3]="'USER_AVATAR'"
    [entityId]="entityId"
    [showReplaceButton]="true"
    [enableWebPConversion]="true"
    (imageLoaded)="onImageLoaded($event)"
    (replaceImage)="onReplaceImage()">
  </app-upload-file>

  For multi-file uploads (course documents):
  <app-upload-file
    [fileTypeInS3]="'COURSE_DOCUMENT'"
    [courseItemId]="courseItemId"
    [showReplaceButton]="false"
    [enableWebPConversion]="true"
    (fileUploaded)="onFileUploaded($event)"
    (fileRemoved)="onFileRemoved($event)">
  </app-upload-file>
-->

<div class="container" *ngIf="!noUploadDropzone && !showFilesOnly" [ngClass]="{ 'fill-space': fillSpace }">
  <div class="content">
    <input
      type="file"
      #fileInput
      (change)="onFileSelected($event)"
      [multiple]="fileTypeInS3 === 'COURSE_DOCUMENT'"
      style="display: none;"
    />

    <div
      class="dropbox"
      (click)="fileInput.click()"
      (dragover)="onDragOver($event)"
      (dragleave)="onDragLeave($event)"
      (drop)="onFileDropped($event)"
      [class.dragging]="isDragging"
    >
      <div class="icon-wrapper" (click)="startUpload($event)">
        <mat-icon class="icon" fontSet="material-icons-round">upload</mat-icon>
      </div>

      <span class="kbody-medium">
        <span class="kbody-medium" style="color: #0097A3">
          Click to upload
        </span>
        or drag and drop
      </span>

      <ng-content select="[overrideFooter]"></ng-content>

      <span *ngIf="hasOverrideFooter" class="kbody-small">
        <span *ngIf="fileTypeInS3 === 'COURSE_DOCUMENT'">
          PDF, XLS, DOC, DOCx, JPG, PNG or WebP (max 10 MB each)
        </span>
        <span *ngIf="fileTypeInS3 !== 'COURSE_DOCUMENT'">
          JPG, PNG or WebP (max 10 MB)
        </span>
      </span>
    </div>

    <div *ngIf="uploadedFileInfo" class="file-info-dropbox">
      <div
        *ngFor="let file of uploadedFileInfo; let idx = index"
        id="extra"
        class="file-info-extra"
      >
        <div class="file-main" style="display: flex; gap: 20px;">
          <div class="icon-wrapper">
            <mat-icon class="icon" fontSet="material-icons-round">
              {{ file.icon }}
            </mat-icon>
          </div>
          <div
            class="file-name-size"
            style="display: flex; flex-direction: column;"
          >
            <span class="kbody-medium">{{ decodeFilename(file.name) }}</span>
            <span class="kbody-small">{{ file.size }}</span>
            <span *ngIf="file.originalSize" class="kbody-small" style="color: #0097A3;">
              Original: {{ file.originalSize }} ({{ file.compressionRatio }} smaller)
            </span>
          </div>
        </div>

        <div class="file-actions" style="display: flex;">
          <div *ngIf="uploadCompleted" class="upload-complete">
            <mat-icon>check_box</mat-icon>
          </div>
          <div
            *ngIf="!uploadCompleted"
            class="icon-wrapper-delete"
            (click)="removeFile($event, idx)"
          >
            <mat-icon class="icon" fontSet="material-icons-round">
              delete_forever
            </mat-icon>
          </div>
        </div>
      </div>

      <div *ngIf="isUploading" class="progress-wrapper">
        <mat-progress-bar
          class="custom-progress-bar"
          mode="determinate"
          [value]="progress"
        ></mat-progress-bar>
        <span class="progress-percent">{{ progress }}%</span>
      </div>
    </div>

    <div *ngIf="maxFileSizeExceeded" class="file-info-dropbox error">
      <div id="extra" class="file-info-extra">
        <div class="file-main" style="display: flex; gap: 20px;">
          <div class="failed-icon-wrapper">
            <mat-icon class="icon" fontSet="material-icons-round">error</mat-icon>
          </div>
          <div
            class="file-error-info"
            style="display: flex; flex-direction: column;"
          >
            <span class="kbody-medium">{{ decodeFilename(attemptedFileName) }}</span>
            <span class="kbody-small">Upload failed, file is too large.</span>
            <span
              class="kbody-small retry-action"
              (click)="onTryAgain($event)"
            >
              Try Again
            </span>
          </div>
        </div>
        <div class="file-actions" style="display: flex;">
          <div class="icon-wrapper-delete" (click)="removeFile($event)">
            <mat-icon class="icon" fontSet="material-icons-round">
              delete_forever
            </mat-icon>
          </div>
        </div>
      </div>
    </div>

    <div *ngIf="wrongFileType" class="file-info-dropbox error">
      <div id="extra" class="file-info-extra">
        <div class="file-main" style="display: flex; gap: 20px;">
          <div class="failed-icon-wrapper">
            <mat-icon class="icon" fontSet="material-icons-round">error</mat-icon>
          </div>
          <div
            class="file-error-info"
            style="display: flex; flex-direction: column;"
          >
            <span class="kbody-medium">{{ decodeFilename(attemptedFileName) }}</span>
            <span class="kbody-small">Upload failed, file is wrong type.</span>
            <span
              class="kbody-small retry-action"
              (click)="onTryAgain($event)"
            >
              Try Again
            </span>
          </div>
        </div>
        <div class="file-actions" style="display: flex;">
          <div class="icon-wrapper-delete" (click)="removeFile($event)">
            <mat-icon class="icon" fontSet="material-icons-round">
              delete_forever
            </mat-icon>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>

<div class="container" *ngIf="!noUploadDropzone && showFilesOnly">
  <div class="content">
    <div *ngIf="existingDocuments.length > 0" style="width:100%">
      <div
        *ngFor="let doc of existingDocuments"
        class="file-info-dropbox"
      >
        <div id="extra" class="file-info-extra">
          <div class="file-main" style="display: flex; gap: 20px;">
            <div class="icon-wrapper">
              <mat-icon class="icon" fontSet="material-icons-round">
                {{ getFileIcon(doc.fileName) }}
              </mat-icon>
            </div>
            <div
              class="file-name-size"
              style="display: flex; flex-direction: column;"
            >
              <span class="kbody-medium">{{ decodeFilename(doc.fileName) }}</span>
              <span class="kbody-small">{{ doc.fileMimeType }}</span>
            </div>
          </div>
          <div class="file-actions" style="display: flex;">
            <div class="download-image-wrapper" (click)="downloadDocument(doc)">
              <mat-icon class="icon" fontSet="material-icons-round">
                download
              </mat-icon>
            </div>
            <div class="delete-image-wrapper" (click)="deleteDocument(doc)">
              <mat-icon class="icon" fontSet="material-icons-round">
                delete_forever
              </mat-icon>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>

<div
  *ngIf="showReplaceButton && noUploadDropzone"
  id="replace-button-wrapper"
>
  <button id="replaceBtn" mat-button (click)="onReplace()">
    Replace Image
  </button>
</div>
